import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
// @ts-expect-error - this will be resolved at build time
import { Box } from "@xsolla/xui-primitives";
import {
  useResolvedTheme,
  useOverlayLayer,
  OverlayLayerProvider,
  MODAL_LAYER_STEP,
} from "@xsolla/xui-core";
import type { ModalRootProps, ModalType } from "./types";

const ENTER_DURATION = 250;
const EXIT_DURATION = 200;

// Per-type enter/exit motion keyed off the data-modal-type attribute that
// Modal sets on its root node: popup fades + scales, bottom-sheet slides up,
// full-screen fades with the overlay. Exits are faster and accelerate
// (industry convention); prefers-reduced-motion drops motion entirely.
const MODAL_ANIMATION_CSS = `
.xui-modal-overlay {
  opacity: 0;
  transition: opacity ${EXIT_DURATION}ms cubic-bezier(0.4, 0, 1, 1);
}
.xui-modal-overlay.xui-modal-open {
  opacity: 1;
  transition: opacity ${ENTER_DURATION}ms cubic-bezier(0, 0, 0.2, 1);
}
.xui-modal-overlay [data-modal-type] {
  transition: transform ${EXIT_DURATION}ms cubic-bezier(0.4, 0, 1, 1);
}
.xui-modal-overlay.xui-modal-open [data-modal-type] {
  transition: transform ${ENTER_DURATION}ms cubic-bezier(0, 0, 0.2, 1);
}
.xui-modal-overlay:not(.xui-modal-open) [data-modal-type="popup"] {
  transform: scale(0.95);
}
.xui-modal-overlay:not(.xui-modal-open) [data-modal-type="bottom-sheet"] {
  transform: translateY(100%);
}
@media (prefers-reduced-motion: reduce) {
  .xui-modal-overlay,
  .xui-modal-overlay.xui-modal-open,
  .xui-modal-overlay [data-modal-type],
  .xui-modal-overlay.xui-modal-open [data-modal-type] {
    transition: none;
    transform: none;
  }
}
`;

export const ModalRoot = memo(({ modals, testID }: ModalRootProps) => {
  const { theme } = useResolvedTheme();
  const modalLayer = useOverlayLayer() + MODAL_LAYER_STEP;
  const visibleModalKey = useMemo(
    () => Object.keys(modals)[Object.keys(modals).length - 1],
    [modals]
  );

  // Retain the last open modal so it stays rendered during the fade-out.
  // NOTE: this ref is intentionally updated during render (not in an effect)
  // so it is always populated on the SAME render that visibleModalKey first
  // becomes undefined. Deriving activeKey/activeModal from the ref directly
  // (without an `exiting` state guard) prevents the one-frame null-return gap
  // that caused the modal to snap away before the CSS transition could start.
  const lastModalRef = useRef<{ key: string; modal: ModalType } | null>(null);
  if (visibleModalKey) {
    lastModalRef.current = {
      key: visibleModalKey,
      modal: modals[visibleModalKey],
    };
  }

  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (visibleModalKey) {
      setExiting(false);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    }
    if (!lastModalRef.current) return;
    setVisible(false);
    setExiting(true);
    const timer = setTimeout(() => {
      lastModalRef.current = null;
      setExiting(false);
    }, EXIT_DURATION);
    return () => clearTimeout(timer);
  }, [visibleModalKey]);

  // Derive from lastModalRef directly so the portal stays mounted on the
  // first render after close (before useEffect has had a chance to set
  // exiting=true). lastModalRef is only null'd after EXIT_DURATION ms.
  const activeKey = visibleModalKey ?? lastModalRef.current?.key;
  const activeModal = visibleModalKey
    ? modals[visibleModalKey]
    : lastModalRef.current?.modal;

  if (!activeKey || !activeModal || typeof document === "undefined")
    return null;

  return ReactDOM.createPortal(
    <>
      <style>{MODAL_ANIMATION_CSS}</style>
      <Box
        testID={testID}
        className={`xui-modal-overlay${visible ? " xui-modal-open" : ""}`}
        position="fixed"
        zIndex={modalLayer}
        top={0}
        left={0}
        right={0}
        bottom={0}
        alignItems="center"
        justifyContent="start"
        padding={20}
        overflowY="auto"
        style={{
          overscrollBehavior: "contain",
          // Disable interaction during exit so clicks do not reach
          // underlying content while the overlay is fading out.
          pointerEvents: !visible && !!activeKey ? "none" : undefined,
        }}
      >
        <Box
          position="fixed"
          zIndex={0}
          top={0}
          left={0}
          right={0}
          bottom={0}
          backgroundColor={theme.colors.layer?.scrim ?? "rgba(0, 0, 0, 0.75)"}
          aria-hidden="true"
        />
        <Box
          position="relative"
          zIndex={1}
          width="100%"
          alignItems="center"
          justifyContent="center"
          margin="auto"
        >
          <OverlayLayerProvider value={modalLayer}>
            <React.Fragment key={activeKey}>{activeModal({})}</React.Fragment>
          </OverlayLayerProvider>
        </Box>
      </Box>
    </>,
    document.body
  );
});

ModalRoot.displayName = "ModalRoot";
